import { ALL_PUSH_NOTIFICATION_FIELDS } from '../../../constants/pushNotificationCategories';

export const GLOBAL_CONFIG_FIELD_LABELS: Record<string, string> = {
  defaultDocumentImportOutlineModel: 'Document Outline Model',
  defaultDocumentImportContentModel: 'Document Content Model',
  defaultFundContentModel: 'Fund Content Model',
  defaultArticleContentModel: 'Article Content Model',
  defaultChatModel: 'Chat Model',
  adminMinimumInvestment: 'Admin Minimum Investment',
  adminInvestmentIncrement: 'Admin Investment Increment',
  investorMinimumInvestment: 'Investor Minimum Investment',
  investorInvestmentIncrement: 'Investor Investment Increment',
  featureFinanceNewsArticles: 'Finance News Articles',
  featureAiChat: 'AI Chat',
  featureVeraGlobalChat: 'Vera Global Chat (Floating Assistant)',
  featureInvestmentEntities: 'Investment Entities',
  featureContentReviews: 'Content Reviews',
  concurrentFrontendStorageRequests: 'Concurrent Storage Requests',
  concurrentFrontendStorageBatchSize: 'Storage Batch Size',
  pushNotificationDelayMinutes: 'Push Notification Delay (minutes)',
  veraPromptProposalNotificationChannel: 'Vera Prompt Proposal Notification Channel',
  veraPromptProposalNotificationGoogleChatWebhookUrl: 'Google Chat Incoming Webhook URL',
  veraPromptProposalNotificationEmail: 'Notification Email',
  veraThinkingBudget: 'Ask Vera Thinking Budget',
  enableVeraGoogleSearchGrounding: 'Vera Global Chat — Google Search',
  enableVeraGlobalChatSearchLinks: 'Vera Global Chat — Show Search Source Links',
  veraGoogleSearchExcludeDomains: 'Google Search Excluded Domains (all Vera search)',
  veraGuardrailMessage: 'Vera Guardrail Message',
  enableNewsFeature: 'News Section (mobile app)',
  newsBlogSourceUrl: 'News Blog Source URL',
  defaultStatementDocumentCategoryId: 'Default Category for Generated Statements',
  minAppVersionIos: 'Minimum iOS App Version',
  minAppVersionAndroid: 'Minimum Android App Version',
};

export const ADVANCED_MODEL_FIELDS = [
  'defaultDocumentImportOutlineModel',
  'defaultDocumentImportContentModel',
  'defaultFundContentModel',
  'defaultArticleContentModel',
  'defaultChatModel',
] as const;

export const VERA_PROMPT_PROPOSAL_FIELDS = [
  'veraPromptProposalNotificationChannel',
  'veraPromptProposalNotificationGoogleChatWebhookUrl',
  'veraPromptProposalNotificationEmail',
] as const;

export const VERA_RESPONSES_FIELDS = [
  'veraGuardrailMessage',
  'veraThinkingBudget',
  'enableVeraGoogleSearchGrounding',
  'enableVeraGlobalChatSearchLinks',
] as const;

export const VERA_EXCLUDED_URL_FIELDS = ['veraGoogleSearchExcludeDomains'] as const;

export const ALL_VERA_FIELDS = [
  ...VERA_PROMPT_PROPOSAL_FIELDS,
  ...VERA_RESPONSES_FIELDS,
  ...VERA_EXCLUDED_URL_FIELDS,
] as const;

/**
 * Longer "(i)" explanations shown next to a field label. Keep the on-screen
 * one-liner in the field's own description; put the "why / where it applies"
 * detail here.
 */
export const GLOBAL_CONFIG_FIELD_HELP: Record<string, string> = {
  veraGuardrailMessage:
    'Shown verbatim to investors whenever Vera refuses a request — advice-seeking, off-topic, prompt-injection, or a reply that drifted into advice — across the floating web chat, the mobile app, and per-fund Q&A. Leave blank to use the built-in default. Markdown is supported; on render, links are restricted to https, mailto, tel and app-relative (/…) URLs and all HTML/images are stripped.',
  veraGoogleSearchExcludeDomains:
    'When Vera answers using Google Search grounding, results from these domains are dropped. Your entries are added to a built-in baseline list (major social networks, forums and user-generated-content sites such as reddit, x.com, medium, linkedin, youtube, quora). One root domain per line — "www." and any path are ignored, so "competitor.com" also covers "www.competitor.com/blog". Applies to both the investor global Vera chat and admin assist. This is a blocklist only; to change what Vera is told about sources, edit the grounding prompt in the Vera sandbox.',
  veraThinkingBudget:
    'Gemini "thinking" token budget for Ask Vera. 0 disables thinking (fastest, best for data lookups); -1 lets the model decide (slower, better for multi-step reasoning); 1–24576 sets a fixed budget.',
  enableVeraGoogleSearchGrounding:
    'Lets the investor floating Vera chat call Google Search to ground answers. Admin assist has its own separate control. The grounding prompt and per-context rules live in the Vera sandbox.',
  enableVeraGlobalChatSearchLinks:
    'When off, the API strips web source links from investor global-chat replies even if grounding still runs. Does not affect admin assist.',
  veraPromptProposalNotificationChannel:
    'How reviewers are alerted when someone submits a Vera prompt proposal from the sandbox. Separate from the proposal list itself — proposals are always saved regardless of this setting.',
  veraPromptProposalNotificationGoogleChatWebhookUrl:
    'Google Chat incoming webhook for the destination space, used when the channel is Google Chat. In Google Chat: open the space → Apps & integrations → Add webhooks → name and save → copy the URL.',
  veraPromptProposalNotificationEmail:
    'Recipient address used when the notification channel is Email.',
};

export const FEATURE_SWITCH_FIELDS = [
  'featureFinanceNewsArticles',
  'featureAiChat',
  'featureVeraGlobalChat',
  'featureInvestmentEntities',
  'featureContentReviews',
  'enableNewsFeature',
] as const;

export const INVESTMENT_CONFIG_FIELDS = [
  'adminMinimumInvestment',
  'adminInvestmentIncrement',
  'investorMinimumInvestment',
  'investorInvestmentIncrement',
] as const;

export const MEDIA_CONTENT_FIELDS = [
  'concurrentFrontendStorageRequests',
  'concurrentFrontendStorageBatchSize',
] as const;

export const MOBILE_APP_FIELDS = ['minAppVersionIos', 'minAppVersionAndroid'] as const;

export const DOCUMENT_CATEGORY_FIELDS = ['defaultStatementDocumentCategoryId'] as const;

export const NOTIFICATION_CONFIG_FIELDS = [
  'pushNotificationDelayMinutes',
  ...ALL_PUSH_NOTIFICATION_FIELDS,
] as const;

/** URL hash values that route to General → Notifications. */
export const NOTIFICATION_HASH_FIELDS = new Set([
  'pushNotificationDelayMinutes',
  'enablePushNotifications',
]);

export function isNotificationHashField(hash: string): boolean {
  return NOTIFICATION_HASH_FIELDS.has(hash) || hash.startsWith('enablePush');
}

export type VeraSubTab = 'proposals' | 'responses' | 'excluded-urls';

const VERA_HASH_FIELD_SUBTAB: Record<string, VeraSubTab> = {
  ...Object.fromEntries(VERA_PROMPT_PROPOSAL_FIELDS.map((f) => [f, 'proposals' as const])),
  ...Object.fromEntries(VERA_RESPONSES_FIELDS.map((f) => [f, 'responses' as const])),
  ...Object.fromEntries(VERA_EXCLUDED_URL_FIELDS.map((f) => [f, 'excluded-urls' as const])),
};

/** If the URL hash targets a Vera field, returns which Vera sub-tab owns it. */
export function veraSubTabForHash(hash: string): VeraSubTab | null {
  return VERA_HASH_FIELD_SUBTAB[hash] ?? null;
}
